<!-- =========================================================================================
  File Name: SaleOrdersReport.vue
  Description: Sale orders report
  ----------------------------------------------------------------------------------------
  Item Name: C4 - User list for management
  Author: Oscar Ramírez O.
  Author URL: https://acentoNET.com
========================================================================================== -->

<template>
  <div>
<!--    <add-new-data-sidebar :isSidebarActive="addNewDataSidebar" :id="editID" @closeSidebar="addNewDataSidebar = false"/>-->
    <div class="vx-col w-full mb-base">
      <vx-card ref="parametersCard" class="mb-2">
        <div class="flex justify-start inline-block align-middle">
          <div class="mr-2" style="min-width: 495px;">
            <ejs-daterangepicker ref="period" :startDate="startDate" :endDate="endDate" floatLabelType="Auto" placeholder="Seleccione período"/>
          </div>
          <div>
            <vs-button radius color="success" type="filled" icon-pack="feather" icon="icon-search" class="ml-4 mt-2" @click="getReport"/>
          </div>
        </div>
      </vx-card>
      <vx-card ref="reportCard" class="mb-2" v-show="gridReady">
        <ejs-grid
          ref="grid"
          :dataSource="data"
          locale='es-GT'
          :toolbar='toolbarOptions'
          :allowPaging="true"
          :pageSettings='pageSettings'
          :allowSorting='true'
          :sortSettings='sortOptions'
          :allowExcelExport='true'
          :toolbarClick='toolbarClick'
          :allowFiltering='true'
          :filterSettings='filterOptions'
        >
          <e-columns>
            <e-column field="id" :visible=false headerText="ID" :isPrimaryKey="true"/>
            <e-column field='date' headerText='Fecha' width="160" :valueAccessor="formatDate"/>
            <e-column field="documentTypeName" headerText="Tipo" width="150" :filter='checkBoxFilter'/>
            <e-column field="number" headerText="Documento" width="170" :template="cTemplate" displayValue="number" routeName="sale_transaction" urlBase="/sale/transaction/"/>
            <e-column field="customerName" headerText="Cliente" width="300" clipMode='EllipsisWithTooltip' :filter='checkBoxFilter'/>
            <e-column field="vat" headerText="NIT" width="200" clipMode='EllipsisWithTooltip' :filter='checkBoxFilter'/>
            <e-column field="nrc" headerText="NRC" width="130" clipMode='EllipsisWithTooltip' :filter='checkBoxFilter'/>
            <e-column field="statusName" headerText="Estado" width="120" :filter='checkBoxFilter'/>
            <e-column field="salesmanName" headerText="Vendedor" width="170" :filter='checkBoxFilter'/>
            <e-column field="discountAmount" headerText="Descuento" textAlign='Right' width="120" :valueAccessor="discountFormatter"/>
            <e-column field="amount" type="number" headerText="Valor" textAlign='Right' format="N2" width="120"/>
            <e-column field="netAmount" type="number" headerText="Valor sin IVA" textAlign='Right' format="N2" width="160"/>
          </e-columns>
          <e-aggregates>
            <e-aggregate>
              <e-columns>
                <e-column type="Sum" field="discountAmount" format="N2"/>
                <e-column type="Sum" field="amount" format="N2"/>
                <e-column type="Sum" field="netAmount" format="N2"/>
              </e-columns>
            </e-aggregate>
          </e-aggregates>
        </ejs-grid>
      </vx-card>
      <vx-card class="mb-2" v-show="gridReady">
        <div class="flex justify-end inline-block align-middle" v-if="printedCount > 0">
          <div class="text-sm font-small">
            Total documentos emitidos ({{ printedCount }}): <b>{{ Intl.NumberFormat().format(printedAmount) }}</b>
          </div>
        </div>
        <div class="flex justify-end inline-block align-middle" v-if="voidCount > 0">
          <div class="text-sm font-small">
            Total documentos anulados ({{ voidCount }}): <b>{{ Intl.NumberFormat().format(voidAmount) }}</b>
          </div>
        </div>
        <div class="flex justify-end inline-block align-middle" v-if="otherCount > 0">
          <div class="text-sm font-small">
            Total otros documentos ({{ otherCount }}): <b>{{ Intl.NumberFormat().format(otherAmount) }}</b>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import * as lang from 'vuejs-datepicker/src/locale';
  // import AddNewDataSidebar from './OrderData';

  import {loadCldr, L10n, setCulture} from '@syncfusion/ej2-base';
  import {GridPlugin, Toolbar, Search, Page, Sort, ExcelExport, Aggregate, Filter} from '@syncfusion/ej2-vue-grids';
  import {DateRangePickerPlugin} from '@syncfusion/ej2-vue-calendars';
  import numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
  import gregorian from 'cldr-data/main/es-GT/ca-gregorian.json';
  import numbers from 'cldr-data/main/es-GT/numbers.json';
  import timeZoneNames from 'cldr-data/main/es-GT/timeZoneNames.json';
  import weekData from 'cldr-data/supplemental/weekData.json';// To load the culture based first day of week
  import CellRendererLink from '@/views/infra/cell-renderer/CRLinkS';

  Vue.use(DateRangePickerPlugin);
  Vue.use(GridPlugin);

  loadCldr(numberingSystems, gregorian, numbers, timeZoneNames, weekData);
  setCulture('es-GT');
  L10n.load({
    'es-GT': {
      'daterangepicker': {
        placeholder: 'Seleccione período',
        startLabel: 'Fecha inicial',
        endLabel: 'Fecha final',
        applyText: 'Seleccionar',
        cancelText: 'Cancelar',
        selectedDays: 'Seleccione día inicial y final',
        days: 'Días'
      },
      'grid': {
        'EmptyRecord': 'No se encontraron transacciones de venta',
        'Search': 'Buscar',
        'Excelexport': 'Exportar'
      },
      'pager': {
        'currentPageInfo': '{0} de {1} páginas',
        'firstPageTooltip': 'Ir a primera página',
        'lastPageTooltip': 'Ir a última página',
        'nextPageTooltip': 'Ir a página siguiente',
        'previousPageTooltip': 'Ir a página anterior'
      }
    }
  });

  export default {
    data() {
      return {
        loaded: false,
        gridReady: false,
        language: lang,
        startDate: null,
        endDate: null,
        data: [],
        editID: null,
        addNewDataSidebar: false,
        dateFormatOptions: {type: 'dateTime', format: 'dd/MM/yyyy hh:mm a'},
        pageSettings: {pageSize: 10},
        toolbarOptions: ['Search', 'ExcelExport'],
        sortOptions: {columns: [{field: 'date', direction: 'Ascending'}, {field: 'number', direction: 'Ascending'}]},
        filterOptions: {
          type: 'Menu'
        },
        checkBoxFilter: {
          type: 'CheckBox'
        },
        cardTitle: 'Listado de ventas',
        cTemplate: function () {
          return { template: CellRendererLink, urlBase: '/sale/transaction/' };
        }
      }
    },
    components: {
      // AddNewDataSidebar,
      CellRendererLink
    },
    provide: {
      grid: [Toolbar, Search, Page, Sort, ExcelExport, Aggregate, Filter]
    },
    computed: {
      api() {
        return this.$store.getters.getParameter('connection').apiURL;
      },
      printedCount() {
        if (this.data.length > 0) {
          return this.data.filter(item => item.statusId != 'ANU').length;
        } else {
          return 0;
        }
      },
      printedAmount() {
        if (this.data.length > 0) {
          if (this.printedCount > 0) {
            return this.data.filter(item => item.statusId != 'ANU').map(item => item.amount).reduce((prev, next) => prev + next);
          } else {
            return 0;
          }
        } else {
          return 0;
        }
      },
      voidCount() {
        if (this.data.length > 0) {
          return this.data.filter(item => item.statusId == 'ANU').length;
        } else {
          return 0;
        }
      },
      voidAmount() {
        if (this.data.length > 0) {
          if (this.voidCount > 0) {
            return this.data.filter(item => item.statusId == 'ANU').map(item => item.amount).reduce((prev, next) => prev + next);
          } else {
            return 0;
          }
        } else {
          return 0;
        }
      },
      otherCount() {
        if (this.data.length > 0) {
          return this.data.filter(item => (item.statusId != 'ANU' && item.statusId != 'EMI')).length;
        } else {
          return 0;
        }
      },
      otherAmount() {
        if (this.data.length > 0) {
          if (this.otherCount > 0) {
            return this.data.filter(item => (item.statusId != 'ANU' && item.statusId != 'EMI')).map(item => item.amount).reduce((prev, next) => prev + next);
          } else {
            return 0;
          }
        } else {
          return 0;
        }
      }
    },
    methods: {
      numericFormat(value, decimals) {
        var ret = value;
        if (typeof value !== 'undefined') {
          ret = value.toFixed(decimals).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        }
        return ret;
      },
      discountFormatter(field, data) {
        return (data[field] == 0 ? '' : data[field].toFixed(2));
      },
      dateFormat(value) {
        var d = new Date(value);
        return `${d.getDate().toString().padStart(2, '0')}/${d.getMonth().toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
      },
      dateISOFormat(value) {
        const d = new Date(value.getTime() - (6 * 60 * 60 * 1000));
        return d.toISOString();
      },
      formatDate: function (field, data) {
        var d = new Date(data[field].replace('Z', ''));
        return d.toLocaleString();
        // return `${d.getDate().toString().padStart(2, '0')}/${d.getMonth().toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
      },
      toolbarClick: function (args) {
        if (args.item.id.includes('excelexport')) { // 'Grid_excelexport' -> Grid component id + _ + toolbar item name
          let excelExportProperties = {
            fileName: "ReporteVentas.xlsx"
          };
          this.$refs.grid.excelExport(excelExportProperties);
        }
      },
      getReport() {
        this.startDate = this.$refs.period.getSelectedRange().startDate;
        this.endDate = this.$refs.period.getSelectedRange().endDate;
        if (this.startDate) {
          if (this.endDate) {
            this.startDate.setHours(0);
            this.startDate.setMinutes(0);
            this.startDate.setSeconds(0);
            this.endDate.setHours(23);
            this.endDate.setMinutes(59);
            this.endDate.setSeconds(59);
            if (this.startDate < this.endDate) {
              this.cardTitle = 'Listado de ventas del ' + this.startDate.toLocaleDateString() + ' al ' + this.endDate.toLocaleDateString();
              this.$vs.loading({
                text: 'Cargando consulta',
                type: 'point'
              });
              var self = this;
              var data = {
                startDate: this.dateISOFormat(this.startDate),
                endDate: this.dateISOFormat(this.endDate),
                userId: this.$store.state.AppActiveUser.userId,
                token: this.$store.state.AppActiveUser.token
              };
              this.$http.post(`${this.api}/reports/sales/orders`, data).then(response => {
                if (response.data.success) {
                  if (response.data.data) {
                    self.data = response.data.data
                  } else {
                    self.data = [];
                  }
                  self.gridReady = true;
                } else {
                  self.$vs.notify({
                    title: 'Error al cargar reporte de órdenes de venta',
                    text: response.data.message,
                    color: 'danger'
                  });
                }
                this.$vs.loading.close();
                self.loaded = true;
              }).catch(error => {
                self.loaded = true;
                this.$vs.loading.close();
                var errorMessage = '';
                if (error.response) {
                  errorMessage = error.response.data.ErrorMessage
                } else {
                  errorMessage = 'Error de conectividad'
                }
                self.$vs.notify({
                  title: 'Error al cargar reporte de órdenes de venta',
                  text: errorMessage,
                  color: 'danger'
                });
              });
            } else {
              this.$vs.notify({
                title: 'Error en parámetros',
                text: 'Fecha final debe ser mayor a fecha inicial',
                color: 'danger'
              });
            }
          } else {
            this.$vs.notify({
              title: 'Error en parámetros',
              text: 'Debe especificar una fecha de finalización',
              color: 'danger'
            });
          }
        } else {
          this.$vs.notify({
            title: 'Error en parámetros',
            text: 'Debe especificar una fecha de inicio',
            color: 'danger'
          });
        }
      },
      refreshData(card) {
        this.getReport();
        if (typeof card !== 'undefined') {
          card.removeRefreshAnimation(500);
        }
      }
    },
    mounted() {
      this.startDate = new Date();
      this.startDate.setHours(0);
      this.startDate.setMinutes(0);
      this.startDate.setSeconds(0);
      this.startDate.setDate(1)
      this.endDate = new Date();
      this.endDate.setHours(23);
      this.endDate.setMinutes(59);
      this.endDate.setSeconds(59);
    }
  }
</script>

<style lang="scss">
  @import '../../../../node_modules/@syncfusion/ej2-base/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-buttons/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-inputs/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-popups/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-navigations/styles/material.css';
  @import '../../../../node_modules/@syncfusion/ej2-lists/styles/material.css';
  @import "../../../../node_modules/@syncfusion/ej2-vue-calendars/styles/material.css";
  @import "../../../../node_modules/@syncfusion/ej2-vue-grids/styles/material.css";
</style>
